import { Box, useMediaQuery, useTheme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import ModalAddParty from 'components/add-contact-modal/add-contact-modal';
import clsx from 'clsx';
import React, { useEffect } from 'react';
import { Redirect, Route, Switch, useHistory } from 'react-router-dom';
import EmptyContainer from './components/EmptyContainer';
import PartyHeader from './components/PartyHeader';
import PartyInfo from './components/PartyInfo';
import PartySide from './components/PartySide';
import useParties from './hooks';

const useStyles = makeStyles((theme: any) => ({
  container: {
    padding: theme.spacing(3),
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(2),
    },
  },
  content: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(3),
    [theme.breakpoints.down('lg')]: {
      gap: theme.spacing(2),
    },
  },
  listPane: {
    width: '100%',
    minWidth: 0,
    [theme.breakpoints.up('lg')]: {
      position: 'sticky',
      top: parseFloat(theme.spacing(10)) + 1,
      flex: '0 0 320px',
      width: 320,
      maxHeight: `calc(100vh - ${parseFloat(theme.spacing(10)) + 1}px - ${theme.spacing(2)})`,
      overflowY: 'auto',
      overscrollBehavior: 'contain',
    },
  },
  detailPane: {
    flex: 1,
    minWidth: 0,
  },
  hidden: {
    display: 'none',
  },
}));

const Parties = () => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  const {
    partiesListRole,
    partiesListName,
    partiesListOthers,
    openAddModal,
    setOpenAddModal,
    handleCreateAssociation,
    handleDeleteAssociation,
    listingEntries,
    path,
    url,
    t,
    handleViewChange,
    partiesList,
    handleFilterParties,
    selectedPartyId,
    isLoading,
    isError,
    refetch,
    isAdding,
    isDeleting,
  } = useParties();

  const showList = isDesktop || !selectedPartyId;
  const showDetail = isDesktop || !!selectedPartyId;

  useEffect(() => {
    if (!isDesktop) {
      window.scrollTo(0, 0);
    }
  }, [selectedPartyId, isDesktop]);

  return (
    <Box className={classes.container}>
      <PartyHeader
        setOpenAddModal={setOpenAddModal}
        partiesCount={isLoading ? undefined : partiesList.length}
      />
      <Box className={classes.content}>
        <Box className={clsx(classes.listPane, !showList && classes.hidden)}>
          <PartySide
            partiesListRole={partiesListRole}
            partiesListName={partiesListName}
            partiesListOthers={partiesListOthers}
            handleDeleteAssociation={handleDeleteAssociation}
            listingEntries={listingEntries}
            handleViewChange={handleViewChange}
            isLoading={isLoading}
            isError={isError}
            onRetry={refetch}
            isEmpty={!isLoading && !isError && partiesList.length === 0}
            selectedId={selectedPartyId}
            isDeleting={isDeleting}
          />
        </Box>
        <Box className={clsx(classes.detailPane, !showDetail && classes.hidden)}>
          <Switch>
            <Route exact path={path}>
              <EmptyContainer t={t} />
            </Route>

            <Route exact path={`${path}/:partyId`}>
              <PartyInfo
                key={selectedPartyId}
                partiesList={partiesList}
                listingEntries={listingEntries}
                handleFilterParties={handleFilterParties}
                onBack={() => history.push(url)}
                partiesLoading={isLoading}
              />
            </Route>

            <Route path="*">
              <Redirect to={url} />
            </Route>
          </Switch>
        </Box>
      </Box>
      {openAddModal && (
        <ModalAddParty
          open={openAddModal}
          loading={isAdding}
          onClose={() => {
            if (!isAdding) {
              setOpenAddModal(false);
            }
          }}
          onClickAddParty={(partyIdToAdd, kind, callback) => {
            handleCreateAssociation(partyIdToAdd, kind, callback);
          }}
          allowedCodeAssociations="allowed_order_associations"
        />
      )}
    </Box>
  );
};

export default Parties;
